import React, { useEffect, useState } from 'react';
import { Modal, Pressable, Text, TextInput, View } from 'react-native';
import { ticketColors } from '../../../shared/theme/ticketColors';
import {
  OperationalFilterFieldLabel,
  OperationalFilterSegmentedRow,
} from '../../../shared/filters/OperationalFilterPrimitives';
import { SettingsSaveCta, hubStyles } from './settingsHubUi';
import { settingsStyles as styles } from '../settingsScreen.styles';
import {
  completePickupDisplayPairing,
  type PickupDisplayProfile,
} from '../../infra/pickupDisplayRepo';

type Props = {
  visible: boolean;
  branchId: string;
  branchName: string;
  initialPairingSecret?: string | null;
  onClose: () => void;
  onSuccess: () => void;
};

export function PairPickupDisplayModal({
  visible,
  branchId,
  branchName,
  initialPairingSecret,
  onClose,
  onSuccess,
}: Props) {
  const [pin, setPin] = useState('');
  const [label, setLabel] = useState('TV Mostrador');
  const [profile, setProfile] = useState<PickupDisplayProfile>('pickup');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setPin('');
    setError(null);
    setLabel('TV Mostrador');
    setProfile('pickup');
  }, [visible, branchId]);

  const submit = async () => {
    const normalizedPin = pin.replace(/\s/g, '');
    if (normalizedPin.length !== 6) {
      setError('El PIN tiene que ser de 6 dígitos.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await completePickupDisplayPairing({
        branchId,
        pin: normalizedPin,
        label: label.trim() || 'Pantalla',
        profile,
        pairingSecret: initialPairingSecret,
      });
      onSuccess();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos vincular la pantalla');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', padding: 24 }}
        onPress={onClose}
      >
        <Pressable
          style={{ backgroundColor: ticketColors.paper, borderRadius: 12, padding: 20, maxWidth: 480, width: '100%', alignSelf: 'center' }}
          onPress={(e) => e.stopPropagation()}
        >
          <Text style={styles.cardTitle}>Vincular pantalla</Text>
          <Text style={styles.captionBlock}>
            En la TV abrí vuelto.ar/retiro. Ingresá el PIN que aparece en pantalla para {branchName || 'esta sucursal'}.
          </Text>

          <View style={hubStyles.controlBlock}>
            <OperationalFilterFieldLabel>PIN (6 dígitos)</OperationalFilterFieldLabel>
            <TextInput
              value={pin}
              onChangeText={(text) => {
                const digits = text.replace(/\D/g, '').slice(0, 6);
                setPin(digits.length > 3 ? `${digits.slice(0, 3)} ${digits.slice(3)}` : digits);
              }}
              keyboardType="number-pad"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              placeholder="482 193"
              style={hubStyles.inputTicket}
              autoFocus
            />
          </View>

          <View style={hubStyles.controlBlock}>
            <OperationalFilterFieldLabel>Nombre</OperationalFilterFieldLabel>
            <TextInput value={label} onChangeText={setLabel} style={hubStyles.inputTicket} />
          </View>

          <View style={hubStyles.controlBlock}>
            <OperationalFilterFieldLabel>Qué mostrar</OperationalFilterFieldLabel>
            <OperationalFilterSegmentedRow
              ticketTone
              layout="stretch"
              options={[
                { value: 'pickup', label: 'Retiro' },
                { value: 'delivery', label: 'Delivery' },
                { value: 'both', label: 'Ambos' },
              ]}
              value={profile}
              onChange={(v) => setProfile(v as PickupDisplayProfile)}
            />
          </View>

          {error ? <Text style={{ color: ticketColors.warn, marginTop: 8 }}>{error}</Text> : null}

          <View style={{ flexDirection: 'row', gap: 12, marginTop: 16, justifyContent: 'flex-end' }}>
            <SettingsSaveCta
              label="Cancelar"
              variant="secondary"
              layoutWide={false}
              onPress={onClose}
              disabled={busy}
            />
            <SettingsSaveCta
              label="Confirmar"
              layoutWide={false}
              onPress={() => void submit()}
              saving={busy}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
